// StoreSellerEdit.js
import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../../context/Translator";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { Row, Col, Card, Form, Button, Alert, Spinner, Image } from "react-bootstrap";
import axios from "axios";
import PageLayout from "../../../../layouts/PageLayout";

export default function StoreSellerEdit() {
    const { t } = useContext(TranslatorContext);
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const BASE_IMG_URL = process.env.REACT_APP_IMG_URL || "http://localhost:8000/storage/";

    const [formData, setFormData] = useState({
        name: "",
        nickname: "",
        email: "",
        phone: "",
        status: true,
        password: "",
        store_id: "",
        store_name: "",
        account_date: "",
        reference: "",
        account_status: "pending",
        account_message: "",
        dob: "",
        cnic: "",
        cnic_expiry_date: "",
        whatsapp: "",
        house_address: "",
        business_registration_number: "",
        ntn_number: "",
        bank_name: "",
        bank_account_title: "",
        bank_account_number: "",
        iban: "",
        opening_balance: 0,
        payment_type: "",
        payment_value: 0,
        storage_preference: "",
        settlement_type: "",
        can_add_product: true,
        product_limit: 0,
        remaining_product_limit: 0,
        dollar_limit: 0,
        is_verified: false,
        rejection_reason: "",
    });

    const [files, setFiles] = useState({
        user_image: null,
        cnic_front_image: null,
        cnic_back_image: null,
        selfie_with_cnic: null,
        store_document: null,
        ntn_document: null,
    });

    const [imagePreviews, setImagePreviews] = useState({
        user_image: null,
        cnic_front_image: null,
        cnic_back_image: null,
        selfie_with_cnic: null,
        store_document: null,
        ntn_document: null,
    });

    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [pageLoading, setPageLoading] = useState(!location.state?.sellerData);
    const [alert, setAlert] = useState(null);

    const getFullImageUrl = (path) => {
        if (!path) return null;
        if (path.startsWith("http://") || path.startsWith("https://")) return path;
        const cleanBaseUrl = BASE_IMG_URL.endsWith("/") ? BASE_IMG_URL : `${BASE_IMG_URL}/`;
        const cleanPath = path.startsWith("/") ? path.substring(1) : path;
        return `${cleanBaseUrl}${cleanPath}`;
    };

    const populateForm = (userData) => {
        const seller = userData.store_seller || {};
        setFormData({
            name: userData.name || "",
            nickname: userData.nickname || "",
            email: userData.email || "",
            phone: userData.phone || "",
            status: userData.status ?? true,
            password: "",
            store_id: seller.store_id || "",
            store_name: seller.store?.name || "",
            account_date: seller.account_date || "",
            reference: seller.reference || "",
            account_status: seller.account_status || "pending",
            account_message: seller.account_message || "",
            dob: seller.dob || "",
            cnic: seller.cnic || "",
            cnic_expiry_date: seller.cnic_expiry_date || "",
            whatsapp: seller.whatsapp || "",
            house_address: seller.house_address || "",
            business_registration_number: seller.business_registration_number || "",
            ntn_number: seller.ntn_number || "",
            bank_name: seller.bank_name || "",
            bank_account_title: seller.bank_account_title || "",
            bank_account_number: seller.bank_account_number || "",
            iban: seller.iban || "",
            opening_balance: seller.opening_balance ?? 0,
            payment_type: seller.payment_type || "",
            payment_value: seller.payment_value ?? 0,
            storage_preference: seller.storage_preference || "",
            settlement_type: seller.settlement_type || "",
            can_add_product: seller.can_add_product ?? true,
            product_limit: seller.product_limit ?? 0,
            remaining_product_limit: seller.remaining_product_limit ?? 0,
            dollar_limit: seller.dollar_limit ?? 0,
            is_verified: seller.is_verified ?? false,
            rejection_reason: seller.rejection_reason || "",
        });

        setImagePreviews({
            user_image: getFullImageUrl(seller.user_image),
            cnic_front_image: getFullImageUrl(seller.cnic_front_image),
            cnic_back_image: getFullImageUrl(seller.cnic_back_image),
            selfie_with_cnic: getFullImageUrl(seller.selfie_with_cnic),
            store_document: getFullImageUrl(seller.store_document),
            ntn_document: getFullImageUrl(seller.ntn_document),
        });
    };

    useEffect(() => {
        if (location.state?.sellerData) {
            populateForm(location.state.sellerData);
            setPageLoading(false);
        } else {
            const fetchSeller = async () => {
                try {
                    const token = localStorage.getItem("authToken");
                    const response = await axios.get(
                        `${process.env.REACT_APP_API_URL}/admin/store-sellers/${id}`,
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    if (response.data?.success) {
                        populateForm(response.data.data);
                    }
                } catch (err) {
                    setAlert({ type: "danger", message: "Failed to load store seller details." });
                } finally {
                    setPageLoading(false);
                }
            };
            fetchSeller();
        }
    }, [id, location.state]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: null }));
        }
    };

    const handleFileChange = (e) => {
        const { name, files: selectedFiles } = e.target;
        if (selectedFiles && selectedFiles[0]) {
            const file = selectedFiles[0];
            setFiles((prev) => ({ ...prev, [name]: file }));
            setImagePreviews((prev) => ({
                ...prev,
                [name]: URL.createObjectURL(file),
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setErrors({});
        setAlert(null);

        const payload = new FormData();
        Object.keys(formData).forEach((key) => {
            if (formData[key] !== null && formData[key] !== "") {
                payload.append(key, typeof formData[key] === "boolean" ? (formData[key] ? "1" : "0") : formData[key]);
            }
        });

        Object.keys(files).forEach((key) => {
            if (files[key]) payload.append(key, files[key]);
        });

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/admin/store-sellers/${id}/update`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            if (response.data?.success) {
                navigate("/admin/store-sellers");
            }
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors || {});
            } else {
                setAlert({
                    type: "danger",
                    message: err.response?.data?.message || "Failed to update store seller.",
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    const renderDocumentField = (label, name, accept = "image/*") => (
        <Col md={4}>
            <Form.Group className="mb-3">
                <Form.Label>{label}</Form.Label>
                <Form.Control type="file" name={name} onChange={handleFileChange} accept={accept} />
                {imagePreviews[name] && (
                    <div className="mt-2 text-center p-2 border rounded bg-light">
                        {imagePreviews[name].toLowerCase().endsWith(".pdf") ? (
                            <a href={imagePreviews[name]} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
                                {t("View PDF Document")}
                            </a>
                        ) : (
                            <Image
                                src={imagePreviews[name]}
                                alt={label}
                                thumbnail
                                style={{ maxHeight: "140px", objectFit: "contain", width: "100%" }}
                            />
                        )}
                    </div>
                )}
            </Form.Group>
        </Col>
    );

    if (pageLoading) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">{t("Loading data...")}</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb d-flex justify-content-between align-items-center">
                            <div>
                                <h3 className="mc-breadcrumb-title">{t("Edit Store Seller")}</h3>
                                <ul className="mc-breadcrumb-list">
                                    <li className="mc-breadcrumb-item">
                                        <Link to="#" className="mc-breadcrumb-link">{t("Home")}</Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">
                                        <Link to="/admin/store-sellers" className="mc-breadcrumb-link">{t("Store Sellers")}</Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">{t("Edit")}</li>
                                </ul>
                            </div>
                            <Button variant="secondary" onClick={() => navigate("/admin/store-sellers")}>
                                {t("Back")}
                            </Button>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    <Card className="border-0 shadow-sm rounded-4 mb-4">
                        <Card.Header className="bg-white py-3">
                            <h5 className="mb-0 fw-bold">{t("Update Store Seller Account")}</h5>
                        </Card.Header>
                        <Card.Body className="p-4">
                            {alert && (
                                <Alert variant={alert.type} dismissible onClose={() => setAlert(null)}>
                                    {alert.message}
                                </Alert>
                            )}

                            <Form onSubmit={handleSubmit}>
                                <Row className="g-3">
                                    {/* User Account Info */}
                                    <Col md={12}><h6 className="fw-bold text-primary">{t("User Information")}</h6></Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>{t("Full Name")} *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="name"
                                                placeholder="e.g. John Doe"
                                                value={formData.name}
                                                onChange={handleChange}
                                                isInvalid={!!errors.name}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.name?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>{t("Nickname")}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="nickname"
                                                placeholder="e.g. Johnny"
                                                value={formData.nickname}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>{t("Email Address")}</Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                placeholder="e.g. seller@store.com"
                                                value={formData.email}
                                                onChange={handleChange}
                                                isInvalid={!!errors.email}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.email?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>{t("Phone Number")}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="phone"
                                                placeholder="e.g. +923001234567"
                                                value={formData.phone}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>{t("Password (Optional)")}</Form.Label>
                                            <Form.Control
                                                type="password"
                                                name="password"
                                                placeholder={t("Min 6 characters to update")}
                                                value={formData.password}
                                                onChange={handleChange}
                                                isInvalid={!!errors.password}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.password?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>{t("Store Name")}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="store_name"
                                                placeholder="e.g. Tech World Store"
                                                value={formData.store_name}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={12}><hr className="my-3" /></Col>
                                    <Col md={12}><h6 className="fw-bold text-primary">{t("Account Verification & Status")}</h6></Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>{t("Account Status")} *</Form.Label>
                                            <Form.Select
                                                name="account_status"
                                                value={formData.account_status}
                                                onChange={handleChange}
                                            >
                                                <option value="pending">{t("Pending")}</option>
                                                <option value="approved">{t("Approved")}</option>
                                                <option value="rejected">{t("Rejected")}</option>
                                                <option value="suspended">{t("Suspended")}</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    {formData.account_status === "rejected" && (
                                        <Col md={8}>
                                            <Form.Group>
                                                <Form.Label className="text-danger">{t("Rejection Reason")} *</Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={1}
                                                    name="rejection_reason"
                                                    placeholder={t("State reasons for rejecting this profile...")}
                                                    value={formData.rejection_reason}
                                                    onChange={handleChange}
                                                    isInvalid={!!errors.rejection_reason}
                                                    required
                                                />
                                                <Form.Control.Feedback type="invalid">{errors.rejection_reason?.[0]}</Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                    )}

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>{t("Account Date")}</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="account_date"
                                                value={formData.account_date}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>{t("Reference")}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="reference"
                                                placeholder="e.g. Agent Name / Code"
                                                value={formData.reference}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4} className="d-flex align-items-center mt-4">
                                        <Form.Check
                                            type="checkbox"
                                            id="is_verified"
                                            name="is_verified"
                                            label={t("Is Verified Profile")}
                                            checked={formData.is_verified}
                                            onChange={handleChange}
                                        />
                                    </Col>

                                    <Col md={12}><hr className="my-3" /></Col>
                                    <Col md={12}><h6 className="fw-bold text-primary">{t("Personal & Identity Info")}</h6></Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>{t("Date of Birth")}</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="dob"
                                                value={formData.dob}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>{t("CNIC Number")} *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="cnic"
                                                placeholder="e.g. 35201-XXXXXXX-X"
                                                value={formData.cnic}
                                                onChange={handleChange}
                                                isInvalid={!!errors.cnic}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.cnic?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>{t("CNIC Expiry Date")}</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="cnic_expiry_date"
                                                value={formData.cnic_expiry_date}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>{t("WhatsApp Number")}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="whatsapp"
                                                placeholder="e.g. +923001234567"
                                                value={formData.whatsapp}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={8}>
                                        <Form.Group>
                                            <Form.Label>{t("House Address")}</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={1}
                                                name="house_address"
                                                placeholder="e.g. House #123, Street 4, City"
                                                value={formData.house_address}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={12}><hr className="my-3" /></Col>
                                    <Col md={12}><h6 className="fw-bold text-primary">{t("Business & Banking Details")}</h6></Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>{t("Business Reg Number")}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="business_registration_number"
                                                placeholder="e.g. REG-123456"
                                                value={formData.business_registration_number}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>{t("NTN Number")}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="ntn_number"
                                                placeholder="e.g. NTN-9876543"
                                                value={formData.ntn_number}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>{t("Bank Name")}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="bank_name"
                                                placeholder="e.g. Meezan Bank"
                                                value={formData.bank_name}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>{t("Bank Account Title")}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="bank_account_title"
                                                placeholder="e.g. John Doe"
                                                value={formData.bank_account_title}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>{t("Bank Account Number")}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="bank_account_number"
                                                placeholder="e.g. 01234567890123"
                                                value={formData.bank_account_number}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>{t("IBAN")}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="iban"
                                                placeholder="e.g. PK36MEZN0001234567890123"
                                                value={formData.iban}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={12}><hr className="my-3" /></Col>
                                    <Col md={12}><h6 className="fw-bold text-primary">{t("Settlement & Payment Configuration")}</h6></Col>

                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label>{t("Settlement Type")}</Form.Label>
                                            <Form.Select
                                                name="settlement_type"
                                                value={formData.settlement_type}
                                                onChange={handleChange}
                                            >
                                                <option value="">{t("Select Type")}</option>
                                                <option value="receivable">{t("Receivable")}</option>
                                                <option value="payable">{t("Payable")}</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label>{t("Payment Type")}</Form.Label>
                                            <Form.Select
                                                name="payment_type"
                                                value={formData.payment_type}
                                                onChange={handleChange}
                                            >
                                                <option value="">{t("Select Payment Type")}</option>
                                                <option value="percentage">{t("Percentage")}</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label>{t("Payment Value")}</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                name="payment_value"
                                                placeholder="e.g. 10.00"
                                                value={formData.payment_value}
                                                onChange={handleChange}
                                                isInvalid={!!errors.payment_value}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.payment_value?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label>{t("Storage Preference")}</Form.Label>
                                            <Form.Select
                                                name="storage_preference"
                                                value={formData.storage_preference}
                                                onChange={handleChange}
                                            >
                                                <option value="">{t("Select Storage Preference")}</option>
                                                <option value="own_store">{t("Own Store")}</option>
                                                <option value="admin_store">{t("Admin Store")}</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label>{t("Opening Balance")}</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                name="opening_balance"
                                                placeholder="0.00"
                                                value={formData.opening_balance}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label>{t("Product Limit")}</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="product_limit"
                                                placeholder="e.g. 500"
                                                value={formData.product_limit}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label>{t("Remaining Product Limit")}</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="remaining_product_limit"
                                                placeholder="e.g. 250"
                                                value={formData.remaining_product_limit}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label>{t("Dollar Limit ($)")}</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                name="dollar_limit"
                                                placeholder="e.g. 5000.00"
                                                value={formData.dollar_limit}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={3} className="d-flex align-items-center mt-4">
                                        <Form.Check
                                            type="checkbox"
                                            id="can_add_product"
                                            name="can_add_product"
                                            label={t("Can Add Product")}
                                            checked={formData.can_add_product}
                                            onChange={handleChange}
                                        />
                                    </Col>

                                    <Col md={12}><hr className="my-3" /></Col>
                                    <Col md={12}><h6 className="fw-bold text-primary">{t("Documents & Media Attachments")}</h6></Col>

                                    {renderDocumentField(t("User Profile Image"), "user_image")}
                                    {renderDocumentField(t("CNIC Front Image"), "cnic_front_image")}
                                    {renderDocumentField(t("CNIC Back Image"), "cnic_back_image")}
                                    {renderDocumentField(t("Selfie with CNIC"), "selfie_with_cnic")}
                                    {renderDocumentField(t("Store Document"), "store_document", "image/*,application/pdf")}
                                    {renderDocumentField(t("NTN Document"), "ntn_document", "image/*,application/pdf")}
                                </Row>

                                <div className="d-flex gap-2 justify-content-end mt-4">
                                    <Button variant="secondary" onClick={() => navigate("/admin/store-sellers")}>
                                        {t("Cancel")}
                                    </Button>
                                    <Button variant="primary" type="submit" disabled={submitting}>
                                        {submitting ? t("Saving...") : t("Update Store Seller")}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </PageLayout>
    );
}